import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SectionEyebrow from "@/components/site/SectionEyebrow";

const FOUNDER_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/db9df231-7ec5-4a7f-90ec-ee5b58d79a2a/0M3A4752.jpg";
const ACUITY = "/book";

const credentials = [
    "Networking Coach & Community Ambassador, Stiletto Boss University (2016–Present)",
    "Founder, Y.E.N. — Youth Entrepreneurs Networking (2018–2020)",
    "Curator, We Meet We Eat&reg; — high-level networking experience for women leaders",
    "Trainer & facilitator for Remington College, Self-Help Credit Union, Carod Properties Realty, She Built This City",
    "Featured speaker & emcee for the Stiletto Boss Pitch Competition, Rich Black Woman Tour, High Vibe Summit, Women's Business Center Conferences",
    "Emcee of Millie CEO Summit",
];

export default function About() {
    return (
        <main className="bg-[#fbf9f8]" data-testid="about-page">
            {/* HERO */}
            <section className="pt-36 pb-20 md:pt-44 md:pb-28 bg-[#fbf9f8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <SectionEyebrow>About Shana Price</SectionEyebrow>
                        <h1 className="font-serif text-5xl md:text-7xl lg:text-[96px] leading-[0.92] tracking-tight text-[#0c0f3d]">
                            I always knew
                            <span className="block italic text-[#e47a6a]">it was people.</span>
                        </h1>
                        <p className="font-serif italic text-xl md:text-2xl text-[#0c0f3d]/70 mt-8 max-w-xl leading-snug">
                            "God is a relationship; we are relationship people."
                        </p>
                        <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mt-2">
                            — Shana Price
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <img
                                src={FOUNDER_IMG}
                                alt="Shana Price"
                                className="w-full aspect-[4/5] object-cover"
                            />
                            <div className="absolute -bottom-5 -left-5 bg-[#0c0f3d] text-[#fbf9f8] px-6 py-4">
                                <p className="font-serif text-xl italic">Founder & CEO</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BIO */}
            <section className="py-20 md:py-28 bg-[#fbf9f8]">
                <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-7">
                    <p className="font-serif text-2xl md:text-3xl text-[#0c0f3d] leading-snug">
                        Shana Price is the Founder and CEO of The Connection Guru&reg;, a relationship-building strategist with more than 30 years of service-driven leadership.
                    </p>
                    <p className="font-sans text-base md:text-lg text-[#0c0f3d]/80 leading-relaxed">
                        She equips students, teams, and leaders with the confidence, communication skills, and social capital they need to thrive in academic, professional, and real-world environments. Her work blends research-based frameworks, high-energy facilitation, and culturally responsive teaching &mdash; creating learning experiences that are engaging, practical, and transformative.
                    </p>
                    <p className="font-sans text-base md:text-lg text-[#0c0f3d]/80 leading-relaxed">
                        Shana&apos;s passion for connection began early. At just seven years old, she delivered her first speaking performance, narrating a full play verbatim at Victory Christian Center. Since then, she has become a sought-after facilitator, speaker, and emcee for organizations and events across the Carolinas and beyond.
                    </p>
                </div>
            </section>

            {/* CREDENTIALS */}
            <section className="py-20 md:py-28 bg-[#0c0f3d] text-[#fbf9f8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <SectionEyebrow color="#ffb5ab">Selected Experience</SectionEyebrow>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight">
                            Three decades of architecting <span className="italic text-[#ffb5ab]">connection.</span>
                        </h2>
                    </div>
                    <ul className="lg:col-span-6 lg:col-start-7 space-y-5">
                        {credentials.map((c, i) => (
                            <li
                                key={i}
                                className="flex gap-5 items-start border-b border-[#6d91ad]/30 pb-5"
                            >
                                <span className="font-serif italic text-[#e47a6a] text-xl leading-tight min-w-[28px]">
                                    0{i + 1}
                                </span>
                                <span
                                    className="font-sans text-base text-[#e3c7b9]/85 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: c }}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* SIGNATURE TOOLS & FRAMEWORKS */}
            <section className="py-20 md:py-28 bg-[#e3c7b9]/40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="mb-12">
                        <SectionEyebrow>Signature Tools &amp; Frameworks</SectionEyebrow>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#0c0f3d] leading-tight">
                            The instruments behind the architecture.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="bg-[#fbf9f8] p-10 md:p-12">
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mb-4">Framework</p>
                            <h3 className="font-serif text-3xl md:text-4xl text-[#0c0f3d] mb-5">The Networking Map&trade;</h3>
                            <p className="font-sans text-base text-[#0c0f3d]/75 leading-relaxed">
                                A practical, research&#8209;backed tool for building intentional, strategic relationships.
                            </p>
                        </div>
                        <div className="bg-[#fbf9f8] p-10 md:p-12">
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mb-4">Companion Tool</p>
                            <h3 className="font-serif text-3xl md:text-4xl text-[#0c0f3d] mb-5">The Networking Bible&reg;</h3>
                            <p className="font-sans text-base text-[#0c0f3d]/75 leading-relaxed">
                                A companion planner designed to help individuals apply connection&#8209;centered habits in real time.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* MISSION QUOTE */}
            <section className="py-24 md:py-36 bg-[#fbf9f8]">
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
                    <span className="font-serif text-[140px] leading-none text-[#e47a6a] block">&ldquo;</span>
                    <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0c0f3d] leading-[1.15] -mt-8">
                        To help people connect with confidence, communicate with clarity, and build relationships that <span className="italic text-[#e47a6a]">open doors.</span>
                    </p>
                    <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mt-10">
                        — Shana&apos;s Mission
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-[#0c0f3d] text-[#fbf9f8]">
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
                    <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1]">
                        Let&apos;s architect what&apos;s <span className="italic text-[#ffb5ab]">next</span>.
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link
                            to={ACUITY}
                            data-testid="about-cta-book-btn"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-[#ffb5ab] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#fbf9f8] transition-colors duration-300"
                        >
                            Book a Consultation
                            <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/services"
                            data-testid="about-cta-services-btn"
                            className="inline-flex items-center gap-3 px-8 py-4 border border-[#fbf9f8] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#fbf9f8] hover:text-[#0c0f3d] transition-colors duration-300"
                        >
                            Explore Services
                            <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
